import * as React from 'react';
import { cn } from '../cn';

export interface AuthShellProps {
  /**
   * Tagline rendered on the brand panel — should be a short marketing line
   * matching the host (e.g. "Modern accounting for growing businesses" on
   * ledgerpro.ai, "Accounting software for Sri Lanka" on ledger.lk).
   */
  tagline: string;
  /**
   * Optional supporting content rendered below the tagline — plain text, or
   * richer block content (e.g. a bullet list). Rendered inside a `<div>`,
   * not a `<p>`, specifically so block-level children (a `<ul>`, a nested
   * `<div>`) don't produce invalid HTML / React hydration warnings.
   */
  subtitle?: React.ReactNode;
  /**
   * Logo rendered top-left on the brand panel and centred on the mobile
   * collapsed header. The same node is used in both — supply something that
   * works at 32px wide.
   */
  logo: React.ReactNode;
  /**
   * Footer content on the brand panel (typically the copyright line, or a
   * richer widget like a locale switcher). Rendered inside a `<div>`, not a
   * `<p>`, for the same block-content reason as {@link subtitle}.
   */
  brandFooter?: React.ReactNode;
  /** The form content rendered in the right-hand pane. */
  children: React.ReactNode;
  className?: string;
}

/**
 * AuthShell — split-pane layout used by login, signup, forgot-password, and
 * reset-password routes.
 *
 * Layout:
 *  - Desktop (`lg:`): two columns — a gradient brand panel on the left
 *    (50%), and a centred form column on the right (50%).
 *  - Mobile: brand panel hidden; the logo collapses to a small badge above
 *    the form column.
 *
 * This is the auth-page archetype called out by Epic 8 §3 / §7. It does NOT
 * cover the OAuth-consent screen or the post-auth onboarding flow — those
 * live in their own routes.
 *
 * The brand-panel gradient reads `var(--accent)`/`var(--accent-deep)`
 * instead of a hardcoded navy pair, so each product theme (Ledger's navy,
 * WagePro's green via `apps/wagepro-web/src/styles/wagepro-theme.css`'s
 * `--accent-deep: var(--wagepro-brand-deep)`, and any future product) gets
 * its own gradient automatically through its existing CSS custom property
 * overrides, per this repo's "extend the shared component, don't fork it"
 * rule (root `CLAUDE.md`'s design-system section). `--accent-deep` is not a
 * new token — it already exists identically in
 * `packages/ledger-tokens/src/web-glass.css` and
 * `apps/ledger-web/src/styles/glass.css` (`#143049`), just not previously
 * consumed here (this component had zero live consumers before the WagePro
 * auth-screen restyle pass added the first one — `apps/ledger-web`'s own
 * `/login`, `/forgot-password`, and `/reset-password` routes render their
 * own separate, hand-rolled `AuthLayout`, not this component, despite
 * `auth_layout.tsx`'s stale docblock claiming otherwise).
 */
export function AuthShell({
  tagline,
  subtitle,
  logo,
  brandFooter,
  children,
  className,
}: AuthShellProps) {
  return (
    <div className={cn('flex min-h-screen bg-white', className)}>
      {/* Left brand panel — hidden under lg */}
      <div className="relative hidden flex-1 overflow-hidden lg:flex">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(160deg, var(--accent) 0%, var(--accent-deep) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">{logo}</div>
          <div>
            <h2 className="text-4xl font-bold leading-tight text-white">
              {tagline}
            </h2>
            {subtitle !== undefined && subtitle !== null && (
              <div className="mt-4 text-base text-white/60">{subtitle}</div>
            )}
          </div>
          {brandFooter !== undefined && brandFooter !== null && (
            <div className="text-xs text-white/30">{brandFooter}</div>
          )}
        </div>
      </div>

      {/* Right form panel — a column, not the centering container itself.
          The mobile header (below) must stay pinned flush at the very top
          regardless of card height; the card area is a SEPARATE flex-1
          child that centers the form within whatever height remains below
          the header. Putting both in one centered block (the earlier
          approach) either left a gap above the header or stranded the card
          near the top with dead space below it — this keeps the two
          concerns independent. */}
      <main className="flex flex-1 flex-col">
        {/* Mobile-only header — full-bleed, flush at the top, same accent
            gradient as the desktop brand panel, so the collapsed mobile view
            still reads as branded chrome rather than a bare logo on white. */}
        <div
          className="flex items-center gap-3 px-6 py-5 lg:hidden"
          style={{
            background:
              'linear-gradient(160deg, var(--accent) 0%, var(--accent-deep) 100%)',
          }}
        >
          {logo}
        </div>

        {/* Card column: fills the remaining height (mobile, below the
            header) or the full panel height (desktop, header is hidden),
            and centers the form within it — the original desktop centering
            behaviour, unchanged. */}
        <div className="flex flex-1 items-center justify-center px-8 py-12">
          <div className="w-full max-w-sm">{children}</div>
        </div>
      </main>
    </div>
  );
}
